import { CheckCircle2, Link as LinkIcon, X } from 'lucide-react';
import { toast } from 'sonner';
import type { ClientSaasLink } from '../../../lib/saas-links-api';
import { formatBRDateTime } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { useAuth } from '../../../hooks/use-auth';
import { useConfirmSaasLink, useRejectSaasLink, useSaasLinks } from '../../../hooks/use-saas-links';
import { extractErrorMessage } from '../../../lib/errors';

interface SaasLinksTabProps {
  clientId: string;
}

function ConfidenceBadge({ confidence }: { confidence: ClientSaasLink['matchConfidence'] }) {
  const styles: Record<ClientSaasLink['matchConfidence'], string> = {
    exact_high: 'border-good/40 bg-good/10 text-good',
    manually_confirmed: 'border-good/40 bg-good/10 text-good',
    awaiting_review: 'border-warn/40 bg-warn/10 text-warn',
    manually_rejected: 'border-bad/40 bg-bad/10 text-bad',
    new_record: 'border-line bg-panel-2 text-muted',
  };
  const labels: Record<ClientSaasLink['matchConfidence'], string> = {
    exact_high: 'Match exato',
    manually_confirmed: 'Confirmado',
    awaiting_review: 'Aguardando review',
    manually_rejected: 'Rejeitado',
    new_record: 'Novo registro',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-[6px] border px-2 py-[2px] text-[10px] font-medium uppercase tracking-wider',
        styles[confidence],
      )}
    >
      {labels[confidence]}
    </span>
  );
}

export function SaasLinksTab({ clientId }: SaasLinksTabProps): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const query = useSaasLinks({ clientId });
  const confirmMut = useConfirmSaasLink();
  const rejectMut = useRejectSaasLink();

  const items = query.data?.items ?? [];

  async function handleConfirm(link: ClientSaasLink) {
    try {
      await confirmMut.mutateAsync(link.id);
      toast.success(`Vínculo ${link.saasProduct.name} confirmado.`);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao confirmar vínculo.'));
    }
  }

  async function handleReject(link: ClientSaasLink) {
    const reason = window.prompt(`Motivo da rejeição (opcional):`, '');
    if (reason === null) return; // cancelado
    try {
      await rejectMut.mutateAsync({ id: link.id, reason: reason || null });
      toast.success('Vínculo rejeitado.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao rejeitar vínculo.'));
    }
  }

  if (query.isLoading) {
    return <div className="text-[12px] text-muted">Carregando vínculos…</div>;
  }
  if (query.isError) {
    return (
      <div className="rounded-[10px] border border-bad/40 bg-bad/10 p-3 text-[12px] text-ink">
        Erro ao carregar vínculos.
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="rounded-[10px] border border-dashed border-line bg-panel-2 py-8 text-center text-[12px] text-muted">
        Sem vínculos com SaaS ainda. Aparece após provisioning ou matching de webhook.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {items.map((link) => (
        <div
          key={link.id}
          className="flex flex-col gap-2 rounded-[10px] border border-line bg-panel-2 p-3"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2">
              <LinkIcon size={14} strokeWidth={1.8} className="mt-1 text-muted" />
              <div>
                <div className="text-[13px] font-medium text-ink">{link.saasProduct.name}</div>
                <code className="text-[11px] text-muted-2">
                  tenant: {link.externalTenantId ?? '—'} · user: {link.externalUserId ?? '—'}
                </code>
              </div>
            </div>
            <ConfidenceBadge confidence={link.matchConfidence} />
          </div>
          <div className="text-[11px] text-muted-2">
            Vinculado em {formatBRDateTime(link.linkedAt)} via{' '}
            <span className="font-mono">{link.linkedVia}</span>
            {link.matchScore !== null && ` · score ${link.matchScore}`}
          </div>
          {isAdmin && link.matchConfidence === 'awaiting_review' && (
            <div className="flex items-center justify-end gap-2 border-t border-line pt-2">
              <button
                type="button"
                onClick={() => void handleReject(link)}
                disabled={rejectMut.isPending}
                className="inline-flex h-7 items-center gap-1 rounded-[6px] border border-bad/40 bg-bad/10 px-2 text-[12px] text-bad hover:bg-bad/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <X size={13} strokeWidth={1.8} />
                Rejeitar
              </button>
              <button
                type="button"
                onClick={() => void handleConfirm(link)}
                disabled={confirmMut.isPending}
                className="inline-flex h-7 items-center gap-1 rounded-[6px] border border-line bg-ink px-2 text-[12px] text-page hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ color: '#09090b' }}
              >
                <CheckCircle2 size={13} strokeWidth={1.8} />
                Confirmar
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
